import { Controller } from "@hotwired/stimulus"

// メニュー名の各行10文字制限・許可文字バリデーション用のStimulusコントローラー
export default class MenuNameValidationController extends Controller {
  static targets = ["input", "inputGroup", "placeholder"]
  static values = {
    maxLength: { type: Number, default: 10 },
  }

  declare inputTargets: HTMLInputElement[]
  declare inputGroupTargets: HTMLElement[]
  declare placeholderTargets: HTMLElement[]
  declare maxLengthValue: number

  private static readonly WARNING_CLASS = "p-menu__menu-item__warning"
  private static readonly HAS_WARNING_CLASS = "p-menu__menu-item__content--has-warning"

  // 許可されない文字を検出する正規表現（サニタイザー TextSanitizer と同じホワイトリスト方式）
  // 許可する文字範囲:
  // - 改行 (U+000A)
  // - ASCII printable (U+0020-U+007E): 半角英数字、基本記号、スペース
  // - 全角スペース・記号 (U+3000-U+303F)
  // - ひらがな (U+3040-U+309F)
  // - カタカナ (U+30A0-U+30FF)
  // - 漢字 (U+4E00-U+9FFF)
  // - 全角英数字・半角カナ (U+FF00-U+FFEF)
  // - ◯ (U+25EF): random_menu_names.ts で使用（フロントエンドのみ許可）
  private static readonly DISALLOWED_PATTERN =
    /[^\n\u0020-\u007E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF00-\uFFEF\u25EF]/

  connect() {
    // 各入力フィールドにイベントリスナーを設定
    this.inputTargets.forEach((input) => {
      input.addEventListener("input", () => this.validateInput(input))
      input.addEventListener("blur", () => this.validateInput(input))
    })
  }

  // 許可されない文字が含まれているかチェック
  private containsDisallowedChars(text: string): boolean {
    return MenuNameValidationController.DISALLOWED_PATTERN.test(text)
  }

  // 入力フィールドのバリデーション
  private validateInput(input: HTMLInputElement) {
    const value = input.value
    const isTooLong = value.length > this.maxLengthValue
    const hasDisallowedChars = this.containsDisallowedChars(value)
    const isValid = !isTooLong && !hasDisallowedChars

    // 縁の色を変更
    if (isValid) {
      input.classList.remove("is-invalid")
    } else {
      input.classList.add("is-invalid")
    }

    // 親のinput-groupを取得して警告メッセージを更新
    const inputGroup = input.closest("[data-menu-name-validation-target='inputGroup']") as HTMLElement | null
    if (inputGroup) {
      this.updateWarningMessage(inputGroup)
    }
  }

  // input-group内の警告メッセージを更新
  private updateWarningMessage(inputGroup: HTMLElement) {
    const inputs = inputGroup.querySelectorAll<HTMLInputElement>("[data-menu-name-validation-target='input']")
    const inputsArray = Array.from(inputs)
    const hasTooLong = inputsArray.some((input) => input.value.length > this.maxLengthValue)
    const hasDisallowedChars = inputsArray.some((input) => this.containsDisallowedChars(input.value))

    // p-menu__menu-item__content要素を取得
    const content = inputGroup.querySelector(".p-menu__menu-item__content") as HTMLElement | null
    if (!content) return

    let warning = content.querySelector(`.${MenuNameValidationController.WARNING_CLASS}`)

    if (hasTooLong || hasDisallowedChars) {
      // 警告メッセージを構築
      const messages: string[] = []
      if (hasTooLong) {
        messages.push(`メニュー名は各行${this.maxLengthValue}文字以内で入力してください`)
      }
      if (hasDisallowedChars) {
        messages.push("使用できない文字が含まれています")
      }
      const warningText = messages.join(" / ")

      if (!warning) {
        warning = document.createElement("span")
        warning.className = MenuNameValidationController.WARNING_CLASS
        // contentの先頭に挿入（入力フィールドの上に表示）
        content.insertBefore(warning, content.firstChild)
        // 2行グリッド用のクラスを追加
        content.classList.add(MenuNameValidationController.HAS_WARNING_CLASS)
      }
      warning.textContent = warningText
    } else if (warning) {
      warning.remove()
      // 2行グリッド用のクラスを削除
      content.classList.remove(MenuNameValidationController.HAS_WARNING_CLASS)
    }
  }

  // フォーム送信時のバリデーション
  validate(event: Event) {
    const tooLongInputs = this.inputTargets.filter(
      (input) => input.value.length > this.maxLengthValue
    )
    const disallowedCharsInputs = this.inputTargets.filter(
      (input) => this.containsDisallowedChars(input.value)
    )

    if (tooLongInputs.length > 0 || disallowedCharsInputs.length > 0) {
      event.preventDefault()

      // 最初の無効な入力にフォーカス
      const firstInvalid = tooLongInputs[0] ?? disallowedCharsInputs[0]
      firstInvalid?.focus()

      // エラーメッセージを構築
      const messages: string[] = []
      if (tooLongInputs.length > 0) {
        messages.push(`メニュー名は各行${this.maxLengthValue}文字まで入力できます。`)
      }
      if (disallowedCharsInputs.length > 0) {
        messages.push("使用できない文字が含まれています。")
      }
      messages.push("入力内容を確認してください。")

      alert(messages.join("\n"))
      return false
    }
  }

  // カスタムplaceholderの表示/非表示を更新
  updatePlaceholder(event: Event) {
    const input = event.target as HTMLInputElement
    const wrapper = input.closest(".p-menu__input-field-wrapper")
    if (!wrapper) return

    const placeholder = wrapper.querySelector("[data-menu-name-validation-target='placeholder']")
    if (!placeholder) return

    if (input.value.length > 0) {
      placeholder.classList.add("is-hidden")
    } else {
      placeholder.classList.remove("is-hidden")
    }
  }
}

